const BACKEND_URL = process?.env?.BACKEND_URL ?? ''
const CHAT_BACKEND_URL = process?.env?.CHAT_BACKEND_URL ?? ''

const buildUrl = (path: string, settings: { withApi?: boolean } = {}) => {
  const { withApi = true } = settings
  const apiPrefix = withApi ? '/api' : ''

  return `${BACKEND_URL}${apiPrefix}${path}`
}

const buildChatUrl = (path: string) => `${CHAT_BACKEND_URL}/api/chat${path}`

let authToken: string | null = null
let currentUserId: string | null = null
const pendingAuthResolvers = new Set<(token: string) => void>()
const pendingUserResolvers = new Set<(userId: string) => void>()

const waitForAuthToken = () => {
  if (authToken) {
    return Promise.resolve(authToken)
  }

  return new Promise<string>(resolve => {
    const resolver = (token: string) => {
      pendingAuthResolvers.delete(resolver)
      resolve(token)
    }

    pendingAuthResolvers.add(resolver)
  })
}

export const setAuthToken = (token: string | null) => {
  authToken = token || ''

  if (authToken) {
    pendingAuthResolvers.forEach(resolver => resolver(authToken!))
    pendingAuthResolvers.clear()
  }
}

export const getAuthToken = () => authToken
export const getCurrentUserId = () => currentUserId

const waitForUserId = () => {
  if (currentUserId) {
    return Promise.resolve(currentUserId)
  }

  return new Promise<string>(resolve => {
    const resolver = (userId: string) => {
      pendingUserResolvers.delete(resolver)
      resolve(userId)
    }

    pendingUserResolvers.add(resolver)
  })
}

export const setCurrentUserId = (userId: string | null) => {
  currentUserId = userId || ''

  if (currentUserId) {
    pendingUserResolvers.forEach(resolver => resolver(currentUserId!))
    pendingUserResolvers.clear()
  }
}

export interface ApiRequestOptions extends RequestInit {
  skipAuth?: boolean
}

export const apiRequest = async (
  path: string,
  options: ApiRequestOptions = {}
) => {
  const { skipAuth = false, headers, ...restOptions } = options

  const requestHeaders = new Headers(headers ?? undefined)

  if (!skipAuth) {
    const token = await waitForAuthToken()
    requestHeaders.set('Authorization', `Bearer ${token}`)
  }

  return fetch(buildUrl(path), {
    ...restOptions,
    headers: requestHeaders
  })
}

export interface ChatApiRequestOptions extends RequestInit {}

export const chatApiRequest = async (
  path: string,
  options: ChatApiRequestOptions = {}
) => {
  const { headers, ...restOptions } = options
  const requestHeaders = new Headers(headers ?? undefined)

  const userId = await waitForUserId()
  requestHeaders.set('X-User-Id', userId)

  return fetch(buildChatUrl(path), {
    ...restOptions,
    headers: requestHeaders
  })
}
